import { useState, useEffect } from 'react';
import type { TokenAnalysisResult } from '../api/bipApi';

const SCROLL_THRESHOLD_PX = 100;

export interface SubHeaderSection {
  id: string;
  label: string;
}

interface SubHeaderProps {
  result: TokenAnalysisResult;
}

function buildSections(result: TokenAnalysisResult): SubHeaderSection[] {
  const sections: SubHeaderSection[] = [];
  if (result.content?.text) {
    sections.push({ id: 'section-token-overview', label: 'Token Overview' });
  }
  if (result.smartMoney) {
    sections.push({ id: 'section-smart-money', label: 'Smart Money Tracking' });
  }
  if (result.adminMod) {
    sections.push({ id: 'section-admin-mod', label: 'Admin & Moderators' });
  }
  sections.push({ id: 'section-tweets', label: 'Tweets' });
  return sections;
}

export default function SubHeader({ result }: SubHeaderProps) {
  const [visible, setVisible] = useState(false);
  const token = result.token;
  const symbol = token?.symbol ?? result.symbol ?? '—';
  const imageUrl = token?.imageUrl ?? result.imageUrl ?? null;
  const sections = buildSections(result);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > SCROLL_THRESHOLD_PX);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const SCROLL_OFFSET_PX = 100;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const targetY = window.scrollY + el.getBoundingClientRect().top - SCROLL_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  };

  return (
    <div
      className={`sub-header ${visible ? 'sub-header--visible' : ''}`}
      aria-hidden={!visible}
    >
      <div className="sub-header-content">
        <div className="sub-header-left">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="sub-header-token-image" />
          ) : (
            <div className="sub-header-token-image sub-header-token-image--placeholder" />
          )}
          <span className="sub-header-symbol">
            {symbol === '—' ? <span className="value-empty">—</span> : symbol}
          </span>
        </div>
        <nav className="sub-header-nav" aria-label="Section navigation">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className="sub-header-nav-item"
              onClick={() => scrollToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
